/* eslint-disable max-len */
import { Form, Formik } from 'formik';
import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useModalShow } from '../../hooks/useModalShow';
import { useAppDispatch } from '../../redux/hooks';
import { createNotification } from '../../redux/slices/notificationSlice';
import {
  abortPromiseOnUnmount,
  isValidCode,
} from '../../services/base.service';
import { ApiErrorModel } from '../../types/ApiErrorModel';
import { DataEntryMode } from '../../types/DataEntryMode';
import { DialogType } from '../../types/DialogTypes';
import {
  ManualInputCrudAction,
  ManualInputPagePropTypes,
} from '../../types/propTypes/ManualInputPagePropTypes';
import {
  capitalizeFirstLetterOnly,
  convertAllWordsToBeginWithCapitals,
} from '../../utilities/textUtilities';
import ActionButton from '../ActionButton/ActionButton';
import ApiErrorPanel from '../ApiErrorPanel/ApiErrorPanel';
import BackBar from '../BackBar/BackBar';
import Loader from '../Loader/Loader';
import Modal from '../Modal/Modal';
import NavigationTabToolbar from '../NavigationTabToolbar/NavigationTabToolbar';
import './ManualInputPage.css';

export const MIP_NEW_ROUTE_NAME = 'new';

const ManualInputPage = <T, F>({
  actions,
  navigationTabs,
  classes,
  loadingOptions,
  parameterOptions,
  routes,
  dataFunctions,
  formDetails,
  config,
  children,
  DEV_MODE,
  REDIRECT_TO_LIST_PAGE,
  customDirtyStateHandler,
}: ManualInputPagePropTypes<T, F> & {
  customDirtyStateHandler?: (
    dirty: boolean,
    hasAnyFieldBeenTouched?: boolean
  ) => boolean;
}): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryParams = useParams();

  let dataEntryMode: DataEntryMode;
  let dataId: string | undefined;
  let dataIdentityId: string | undefined;

  const [currentReviewStatus, setCurrentReviewStatus] = useState<string>('');

  useEffect(() => {
    // Only set if objectVerbiage is Senior Snapshot or Exit Adjustment
    if (
      (config.objectVerbiage === 'Senior Snapshot' ||
        config.objectVerbiage === 'Exit Adjustment') &&
      formDetails.initialFormValues &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (formDetails.initialFormValues as any).reviewStatus !== null &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (formDetails.initialFormValues as any).reviewStatus !== undefined
    ) {
      setCurrentReviewStatus(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formDetails.initialFormValues as any).reviewStatus || ''
      );
    }
    // You may want to add dependencies on config.objectVerbiage
    // and formDetails.initialFormValues
  }, [config.objectVerbiage, formDetails.initialFormValues]);

  if (actions === ManualInputCrudAction.CREATE_ONLY) {
    dataEntryMode = DataEntryMode.NEW;
  } else {
    dataEntryMode = config.dataEntryMode;
    dataId = queryParams[parameterOptions.routeParameterName];
    dataIdentityId = parameterOptions.identityName;
  }

  // Used for useEffect
  const isFormModelEmpty = formDetails.isFormModelEmpty;
  const loadingDetailFunc = dataFunctions.loadDataDetailById;
  const loadingFormFunc = dataFunctions.loadFormDetail;
  const clearFunc = dataFunctions.clearData;
  const setEntryMode = dataFunctions.setEntryMode;

  const isLoading = loadingOptions.isLoading;
  const pageClasses = classes?.pageClass ? classes?.pageClass : '';
  const contentClasses = classes?.contentClass ? classes?.contentClass : '';
  const loaderText = loadingOptions?.loadingText || 'Loading';

  const getSubmitButtonText = (): string | undefined => {
    if (
      config.objectVerbiage === 'Senior Snapshot' ||
      config.objectVerbiage === 'Exit Adjustment'
    ) {
      return currentReviewStatus !== '' ? 'Save' : 'Submit for Approval';
    }
    return dataEntryMode == DataEntryMode.NEW ? 'Create' : 'Save';
  };

  const getNotificationText = (): string => {
    if (
      config.objectVerbiage === 'Senior Snapshot' ||
      config.objectVerbiage === 'Exit Adjustment'
    ) {
      if (
        currentReviewStatus === 'Approved' ||
        currentReviewStatus === 'Denied'
      ) {
        if (config.objectVerbiage === 'Exit Adjustment') {
          return 'Exit Adjustment saved';
        }
        return `${capitalizeFirstLetterOnly(config.objectVerbiage)} saved`;
      }
      return `${capitalizeFirstLetterOnly(
        config.objectVerbiage
      )} submitted for approval`;
    }

    if (dataEntryMode == DataEntryMode.NEW) {
      return `New ${capitalizeFirstLetterOnly(config.objectVerbiage)} created`;
    } else {
      return `${capitalizeFirstLetterOnly(config.objectVerbiage)} edited`;
    }
  };
  const getSubHeaderText = (): string =>
    config.subHeading ||
    convertAllWordsToBeginWithCapitals(config.objectVerbiage);

  const [error, setError] = useState<ApiErrorModel | undefined>(undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { show, setShow } = useModalShow();
  const [showSubmitForApproval, setShowSubmitForApproval] = useState(false);
  const [showDuplicatedErrorModal, setShowDuplicatedErrorModal] =
    useState(false);
  const [currentFormValues, setCurrentFormValues] = useState<T | null>(null);

  const handleOpenDuplicatedErrorModal = (): void => {
    setShowDuplicatedErrorModal(true);
  };
  const handleCloseDuplicatedErrorModal = (): void => {
    setShowDuplicatedErrorModal(false);
  };

  const handleOpenSubmitForApprovalModal = (
    reviewStatus: string,
    values: T
  ): void => {
    setCurrentReviewStatus(reviewStatus);
    setCurrentFormValues(values);
    setShowSubmitForApproval(true);
  };
  const handleCloseSubmitForApprovalModal = (): void => {
    setShowSubmitForApproval(false);
  };

  const handleSubmitForApproval = async (): Promise<void> => {
    if (!currentFormValues) return;

    setShowSubmitForApproval(false);
    setIsSaving(true);

    const buttonAction =
      actions === ManualInputCrudAction.CREATE_ONLY
        ? dataFunctions.onCreate
        : dataEntryMode == DataEntryMode.NEW
        ? dataFunctions.onCreate
        : dataFunctions.onUpdate;
    const result = await buttonAction(currentFormValues);

    if (isValidCode(result.status)) {
      setError(undefined);
      dispatch(
        createNotification({
          severity: 'success',
          children: getNotificationText(),
        })
      );

      dataFunctions.setData &&
        dispatch(dataFunctions.setData(currentFormValues));

      if (dataEntryMode == DataEntryMode.NEW) {
        const newId = result.data as string;
        if (REDIRECT_TO_LIST_PAGE) {
          navigate(routes.cancelRoute);
        } else {
          navigate(routes.createSuccessRoute(newId));
        }
      }

      if (dataEntryMode == DataEntryMode.EDIT) {
        if (REDIRECT_TO_LIST_PAGE) {
          navigate(routes.cancelRoute);
        }
      }
    } else {
      if (result.error.status !== 409) {
        if (result.error.title !== 'Duplicate Entry') {
          setError(result.error);
        }
      } else {
        handleOpenDuplicatedErrorModal();
      }
    }

    setIsSaving(false);
  };

  useEffect(() => {
    if (
      actions !== ManualInputCrudAction.CREATE_ONLY &&
      dataId === MIP_NEW_ROUTE_NAME
    ) {
      setEntryMode && dispatch(setEntryMode(DataEntryMode.NEW));
      clearFunc && dispatch(clearFunc());
    }
  }, [actions, dataId, dispatch, clearFunc, setEntryMode]);

  useEffect(() => {
    let promise: unknown = undefined;
    if (
      actions !== ManualInputCrudAction.CREATE_ONLY &&
      dataId &&
      dataId !== MIP_NEW_ROUTE_NAME
    ) {
      setEntryMode && dispatch(setEntryMode(DataEntryMode.EDIT));

      if (dataIdentityId !== dataId) {
        clearFunc && dispatch(clearFunc());
        promise = loadingDetailFunc && dispatch(loadingDetailFunc(dataId));
      }
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [
    actions,
    dataId,
    dataIdentityId,
    loadingDetailFunc,
    clearFunc,
    setEntryMode,
    dispatch,
  ]);

  useEffect(() => {
    let promise: unknown = undefined;
    if (isFormModelEmpty) {
      promise = dispatch(loadingFormFunc(queryParams.schoolYear ?? ''));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch, isFormModelEmpty, loadingFormFunc, queryParams.schoolYear]);

  const handleSave = async (values: T): Promise<void> => {
    setIsSaving(true);

    const buttonAction =
      actions === ManualInputCrudAction.CREATE_ONLY
        ? dataFunctions.onCreate
        : dataEntryMode == DataEntryMode.NEW
        ? dataFunctions.onCreate
        : dataFunctions.onUpdate;
    const result = await buttonAction(values);

    if (isValidCode(result.status)) {
      setError(undefined);
      dispatch(
        createNotification({
          severity: 'success',
          children: getNotificationText(),
        })
      );

      dataFunctions.setData && dispatch(dataFunctions.setData(values));

      if (dataEntryMode == DataEntryMode.NEW) {
        const newId = result.data as string;
        if (REDIRECT_TO_LIST_PAGE) {
          navigate(routes.cancelRoute);
        } else {
          navigate(routes.createSuccessRoute(newId));
        }
      }

      if (dataEntryMode == DataEntryMode.EDIT) {
        if (REDIRECT_TO_LIST_PAGE) {
          navigate(routes.cancelRoute);
        }
      }
    } else {
      if (result.error.status !== 409) {
        if (result.error.title !== 'Duplicate Entry') {
          setError(result.error);
        }
      } else {
        handleOpenDuplicatedErrorModal();
      }
    }

    setIsSaving(false);
  };

  const handleDialogClose = (): void => {
    setShow(false);
  };

  const getSubmitDisabled = (
    isValid: boolean,
    dirty: boolean
  ): boolean | undefined => {
    return (
      !isValid || isSaving || (dataEntryMode == DataEntryMode.EDIT && !dirty)
    );
  };

  const getSubmitButtonTooltip = (
    isValid: boolean,
    dirty: boolean
  ): string | undefined => {
    if (getSubmitDisabled(isValid, dirty)) {
      if (dataEntryMode == DataEntryMode.EDIT && !dirty && isValid) {
        return '** No changes have been made **';
      }
      return '** Make sure all required fields are complete **';
    } else {
      if (
        config.objectVerbiage === 'Senior Snapshot' ||
        config.objectVerbiage === 'Exit Adjustment'
      ) {
        if (currentReviewStatus === '') {
          return `Submit ${capitalizeFirstLetterOnly(
            config.objectVerbiage
          )} for approval`;
        }

        if (dataEntryMode == DataEntryMode.NEW) {
          return `Create New ${convertAllWordsToBeginWithCapitals(
            config.objectVerbiage
          )}`;
        } else {
          return 'Save Changes';
        }
      } else {
        if (dataEntryMode == DataEntryMode.NEW) {
          return `Create New ${convertAllWordsToBeginWithCapitals(
            config.objectVerbiage
          )}`;
        } else {
          return 'Save Changes';
        }
      }
    }
  };

  const handleCancelClick = async (isDirty: boolean): Promise<void> => {
    if (!isDirty) {
      await dataFunctions.onCancel?.();
      navigate(routes.cancelRoute);
    } else {
      setShow(true);
    }
  };

  const handleLeavePage = async (): Promise<void> => {
    await dataFunctions.onCancel?.();
    navigate(routes.cancelRoute);
  };

  return (
    <>
      <div
        data-testid={'manual-input-page'}
        className={`page--grid manual-input ${pageClasses}`}
      >
        {routes.backBarRoute && (
          <BackBar to={routes.backBarRoute} visibleOverride={true} />
        )}
        <h2 className="page--heading">
          <span className="page-heading-content" data-testid="page-header">
            {config.pageHeader}
          </span>
        </h2>
        <div className="page--tab-bar">
          {navigationTabs && (
            <NavigationTabToolbar navigationTabs={navigationTabs} />
          )}
        </div>
        {isLoading ? (
          <div className="manual-loading-container">
            <Loader
              dataTestId={loadingOptions?.loadingDataId}
              message={loaderText}
            />
          </div>
        ) : (
          <div className="page--content">
            <span
              data-testid="content-panel"
              className={`page--content ${contentClasses}`}
            >
              <Formik
                enableReinitialize={true}
                validateOnChange={true}
                validateOnMount={true}
                validationSchema={formDetails.validationSchema}
                initialValues={formDetails.initialFormValues as Object}
                onSubmit={async (formValues) => {
                  await handleSave(formValues as T);
                }}
              >
                {({ dirty, isValid, values, touched, errors }) => {
                  const hasAnyFieldBeenTouched =
                    Object.keys(touched).length > 0;
                  const effectiveDirty = customDirtyStateHandler
                    ? customDirtyStateHandler(dirty, hasAnyFieldBeenTouched) &&
                      dirty
                    : dirty;

                  return (
                    <Form>
                      <div className="heading-button-error-container">
                        <div className="manual-button-container">
                          <span
                            className="subheading"
                            data-testid="page-subheading"
                          >
                            {getSubHeaderText()}
                          </span>
                          <div className="button-group">
                            <p className="required-label">* Required Field</p>
                            <ActionButton
                              classes="button no-wrap-text cancel-button"
                              onClick={() => handleCancelClick(effectiveDirty)}
                              dataTestId="cancel-button"
                              tooltipText={'Navigate to the last page'}
                            >
                              <>Cancel</>
                            </ActionButton>
                            <ActionButton
                              buttonType={
                                config.objectVerbiage === 'Senior Snapshot' ||
                                config.objectVerbiage === 'Exit Adjustment'
                                  ? 'button'
                                  : 'submit'
                              }
                              classes="button--secondary no-wrap-text submit-button"
                              onClick={(x: React.MouseEvent) => {
                                if (
                                  config.objectVerbiage === 'Senior Snapshot' ||
                                  config.objectVerbiage === 'Exit Adjustment'
                                ) {
                                  // Prevent default form submission and show modal instead
                                  x.preventDefault();
                                  handleOpenSubmitForApprovalModal(
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    (values as any)?.reviewStatus,
                                    values as T
                                  );
                                  return;
                                }
                                // Otherwise, let Formik handle the submit (no-op)
                              }}
                              dataTestId="new-submit-button"
                              disabled={getSubmitDisabled(isValid, dirty)}
                              tooltipText={getSubmitButtonTooltip(
                                isValid,
                                dirty
                              )}
                              loading={isSaving}
                            >
                              <>{getSubmitButtonText()}</>
                            </ActionButton>
                          </div>
                        </div>
                        {error && (
                          <ApiErrorPanel
                            error={error}
                            containerClass={'page-error-panel'}
                            includeTitle={true}
                            supportedNonGenericErrors={
                              config.supportedNonGenericErrors
                            }
                          />
                        )}
                      </div>
                      <div>{children}</div>
                      {DEV_MODE && (
                        <div data-testid="dev-mode-panel">
                          {(() => {
                            let valuesString = '';
                            try {
                              valuesString = JSON.stringify(values, null, 2);
                            } catch (e) {
                              console.warn(
                                'Cyclic reference detected in form values:',
                                values
                              );
                              valuesString =
                                '[Cyclic reference detected in form values]';
                            }
                            return <pre>Values: {valuesString}</pre>;
                          })()}
                          <pre>Touched: {JSON.stringify(touched, null, 2)}</pre>
                          <pre>Errors: {JSON.stringify(errors, null, 2)}</pre>
                          <pre>Is Valid: {isValid.toString()}</pre>
                          <pre>Is Dirty: {dirty.toString()}</pre>
                        </div>
                      )}
                    </Form>
                  );
                }}
              </Formik>
            </span>
          </div>
        )}
      </div>

      {show && (
        <Modal
          type={DialogType.CONFIRM}
          open={show}
          onClose={handleDialogClose}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleLeavePage}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleDialogClose}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
      {showSubmitForApproval && (
        <Modal
          type={DialogType.CONFIRM}
          open={showSubmitForApproval}
          onClose={handleCloseSubmitForApprovalModal}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              {(config.objectVerbiage === 'Senior Snapshot' ||
                config.objectVerbiage === 'Exit Adjustment') &&
                (currentReviewStatus === 'Pending Approval' ||
                currentReviewStatus === ''
                  ? 'Are you sure you want to submit for approval?'
                  : currentReviewStatus === 'Approved'
                  ? `Are you sure you want to Approve ${config.objectVerbiage} Submission? `
                  : currentReviewStatus === 'Denied'
                  ? `Are you sure you want to Deny ${config.objectVerbiage} Submission? `
                  : 'Are you sure you want to continue?')}
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleSubmitForApproval}
                dataTestId="yes-button"
                tooltipText={'Submit for approval'}
              >
                <span>
                  {(config.objectVerbiage === 'Senior Snapshot' ||
                    config.objectVerbiage === 'Exit Adjustment') &&
                    (currentReviewStatus === 'Pending Approval' ||
                    currentReviewStatus === ''
                      ? 'Yes, Save & Submit'
                      : currentReviewStatus === 'Approved'
                      ? 'Yes, Save & Approve'
                      : currentReviewStatus === 'Denied'
                      ? 'Yes, Save & Deny'
                      : 'Yes')}
                </span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleCloseSubmitForApprovalModal}
                dataTestId="no-button"
                tooltipText={
                  config.objectVerbiage === 'Exit Adjustment'
                    ? 'Stay on Exit Adjustment'
                    : 'Stay on existing page'
                }
              >
                <span>
                  {(config.objectVerbiage === 'Senior Snapshot' ||
                    config.objectVerbiage === 'Exit Adjustment') &&
                    (currentReviewStatus === 'Pending Approval' ||
                    currentReviewStatus === ''
                      ? 'No, Cancel Submission'
                      : currentReviewStatus === 'Approved'
                      ? 'No, Cancel Approval'
                      : currentReviewStatus === 'Denied'
                      ? 'No, Cancel Denial'
                      : 'No')}
                </span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
      {showDuplicatedErrorModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showDuplicatedErrorModal}
          onClose={handleCloseDuplicatedErrorModal}
          title={'Course Code in use'}
        >
          <div className="confirm-container">
            <p data-testid="duplicated-course-code-error-message">
              Please enter a different 6-digit number and try again.
            </p>
            <div className="button-row">
              <ActionButton
                classes="button--secondary"
                onClick={handleCloseDuplicatedErrorModal}
                dataTestId="ok-button"
                tooltipText={'OK'}
              >
                <span>OK</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ManualInputPage;
